import { useState, useEffect } from "react"
import { PlusCircle } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { SubjectStats } from "../components/ui/school/subject-management/subject-stats"
import { SubjectTable } from "../components/ui/school/subject-management/subject-table"
import { AddSubjectModal } from "../components/ui/school/subject-management/add-subject-modal"
import { EditSubjectModal } from "../components/ui/school/subject-management/edit-subject-modal"
import { DeleteConfirmModal } from "../components/ui/school/subject-management/delete-confirm-modal"
import { listSubjects, createSubject, updateSubject, deleteSubject } from "../hooks/api/schoolApi"

const SubjectManagementPage = () => {
  const [subjects, setSubjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedSubject, setSelectedSubject] = useState(null)
  const [deleteSubjectId, setDeleteSubjectId] = useState("")

  useEffect(() => {
    listSubjects()
      .then((data) =>
        setSubjects(
          data.map((s) => ({
            id: s._id,
            name: s.name,
            code: s.code ?? "",
            enrolled: s.classLevels?.length ?? s.enrolled ?? 0,
            teacher: s.teacherIds?.[0] ?? s.teacher ?? "Unassigned",
            teacherAvatar: s.teacherAvatar ?? `https://api.dicebear.com/7.x/avataaars/svg?seed=${s.name}`,
            icon: s.icon ?? "book",
            color: s.color ?? "blue",
            description: s.description ?? "",
          }))
        )
      )
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const totalSubjects = subjects.length
  const activeTeachers = new Set(subjects.map((s) => s.teacher)).size
  const totalEnrollment = subjects.reduce((sum, s) => sum + (s.enrolled ?? 0), 0)

  const handleAddSubject = async (newSubject) => {
    const saved = await createSubject(newSubject).catch(() => null)
    const entry = saved ?? { id: Date.now().toString(), ...newSubject }
    setSubjects((prev) => [...prev, { ...entry, id: saved?._id ?? entry.id }])
  }

  const handleEditSubject = (subject) => {
    setSelectedSubject(subject)
    setIsEditModalOpen(true)
  }

  const handleUpdateSubject = async (updatedSubject) => {
    await updateSubject(updatedSubject.id, updatedSubject).catch(() => {})
    setSubjects((prev) => prev.map((s) => (s.id === updatedSubject.id ? updatedSubject : s)))
  }

  const handleDeleteClick = (id) => {
    setDeleteSubjectId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    await deleteSubject(deleteSubjectId).catch(() => {})
    setSubjects((prev) => prev.filter((s) => s.id !== deleteSubjectId))
    setIsDeleteModalOpen(false)
    setDeleteSubjectId("")
  }

  const subjectToDelete = subjects.find((s) => s.id === deleteSubjectId)

  return (
    <SchoolLayout>
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl font-bold text-secondary mb-2">Subject Management</h1>
            <p className="text-muted-foreground">
              Manage curriculum and course details for the upcoming academic session.
            </p>
          </div>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            Add Subject
          </Button>
        </div>

        <SubjectStats
          totalSubjects={totalSubjects}
          activeTeachers={activeTeachers}
          totalEnrollment={totalEnrollment}
        />

        {loading ? (
          <div className="flex justify-center py-12 text-gray-400">Loading subjects…</div>
        ) : (
          <SubjectTable subjects={subjects} onEdit={handleEditSubject} onDelete={handleDeleteClick} />
        )}
      </div>

      <AddSubjectModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddSubject} />

      <EditSubjectModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedSubject(null) }}
        onUpdate={handleUpdateSubject}
        subject={selectedSubject}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteSubjectId("") }}
        onConfirm={handleConfirmDelete}
        subjectName={subjectToDelete?.name || ""}
      />
    </SchoolLayout>
  )
}

export default SubjectManagementPage
